import { useEffect, useState } from 'react';
import { api, mediaUrl, formatMoney } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { usePromptLogin } from '@/context/LoginModalContext';
import {
    Search, ShoppingBag, Wallet, Gift, Truck, Users, Radio, Store, Zap, Gavel, ShieldCheck, Shirt, Crown, FileText,
    Sparkles, MessagesSquare, Languages, BadgeCheck, ArrowRight, Star, Package as PackageIcon, TrendingUp, MapPin, ExternalLink,
} from 'lucide-react';

const SIGNUP_URL = 'https://app.okgo.mx/signup?q=signup&type=Cliente&step=1';
const openSignup = () => window.open(SIGNUP_URL, '_blank', 'noopener,noreferrer');

// ---------- HERO (replica del home, botón Go -> signup externo) ----------
const HeroCarousel = ({ images, onCta, isLoginMode }) => {
    const [idx, setIdx] = useState(0);
    const [q, setQ] = useState('');

    useEffect(() => {
        if (!images.length) return;
        const t = setInterval(() => setIdx(i => (i + 1) % images.length), 5000);
        return () => clearInterval(t);
    }, [images.length]);

    const submit = (e) => { e.preventDefault(); onCta(); };

    return (
        <section className="relative w-full overflow-hidden bg-black" data-testid="superpoderes-hero">
            <div className="relative h-[64vh] min-h-[460px] max-h-[760px]">
                {images.map((src, i) => (
                    <div key={i} className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
                        style={{ backgroundImage: `url(${src})` }} />
                ))}
                <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/45 to-black/80" />
                <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-10 text-white text-center">
                    {/* Brand logo with illuminated glow */}
                    <div className="mb-5 relative">
                        <div className="absolute inset-0 rounded-2xl blur-2xl opacity-40 bg-white scale-110" />
                        <div className="relative rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 px-6 py-3 shadow-2xl"
                            style={{ boxShadow: '0 0 32px rgba(255,255,255,0.2), 0 0 64px rgba(255,255,255,0.08)' }}>
                            <img src="/assets/oktelovendoligth.png" alt="Ok te lo Vendo" className="h-10 sm:h-14 w-auto" />
                        </div>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1 text-xs uppercase tracking-widest">
                        <Sparkles className="h-3 w-3" /> Super Poderes de OkGo
                    </span>
                    <h1 className="mt-4 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl drop-shadow-lg" style={{ fontFamily: 'Space Grotesk, Figtree, sans-serif' }}>
                        Todo lo que puedes hacer en Ok te lo vendo.
                    </h1>
                    <p className="mt-3 text-base sm:text-lg text-white/90 max-w-2xl">
                        Marketplace, envíos, wallet, live shopping, servicios y más. Descubre tus superpoderes.
                    </p>
                    <form onSubmit={submit} className="mt-8 w-full max-w-3xl">
                        <div className="flex items-center rounded-full bg-white shadow-2xl overflow-hidden pl-5 pr-1.5 py-1.5">
                            <Search className="h-5 w-5 text-neutral-500 shrink-0" />
                            <input
                                value={q} onChange={(e) => setQ(e.target.value)}
                                placeholder="¿Qué buscas hoy?"
                                className="flex-1 h-12 px-3 text-neutral-900 text-base sm:text-lg bg-transparent outline-none"
                                data-testid="superpoderes-hero-search-input"
                            />
                            <button
                                type="submit"
                                data-testid="hero-search-go-button"
                                className="h-12 px-6 sm:px-8 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-lg tracking-wide transition-[background-color,box-shadow] active:scale-[0.98]"
                            >
                                GO
                            </button>
                        </div>
                    </form>
                    <div className="mt-6 flex flex-wrap gap-2 justify-center">
                        <Button
                            onClick={onCta}
                            size="lg"
                            className="rounded-xl bg-white text-red-700 hover:bg-red-50 shadow-md"
                            data-testid="superpoderes-register-cta-hero"
                        >
                            {isLoginMode ? 'Accede a tu cuenta' : 'Regístrate gratis'} <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, i) => (
                            <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
                                className={`h-1.5 rounded-full transition-[background-color,width] duration-300 ${i === idx ? 'w-8 bg-white' : 'w-3 bg-white/40 hover:bg-white/70'}`} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ---------- CTA STRIP ----------
const CTARegisterStrip = ({ variant = 'red', testId, onCta, isLoginMode }) => (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 my-10 sm:my-14">
        <div className={`rounded-3xl overflow-hidden ${variant === 'red' ? 'bg-gradient-to-r from-red-600 via-red-500 to-rose-600 text-white' : 'bg-slate-900 text-white'} p-6 sm:p-10 flex flex-col md:flex-row items-center gap-4 md:gap-6 shadow-xl`}>
            <div className="flex-1">
                <h3 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Empieza gratis en menos de 60 segundos
                </h3>
                <p className="text-white/85 text-sm mt-1">Crea tu cuenta, elige un plan y desbloquea todos los superpoderes.</p>
            </div>
            <Button onClick={onCta} size="lg" className="rounded-xl bg-white text-red-700 hover:bg-red-50 font-semibold shadow" data-testid={testId || 'register-cta-strip'}>
                {isLoginMode ? 'Acceder' : 'Regístrate'} <ExternalLink className="h-4 w-4 ml-1" />
            </Button>
        </div>
    </section>
);

// ---------- SUPERPOWERS DATA ----------
const SUPERPOWERS = [
    {
        id: 'marketplace-c2c',
        icon: ShoppingBag,
        color: 'bg-red-50 text-red-700 border-red-100',
        title: 'Marketplace C2C',
        value: 'Publica tus productos en segundos.',
        bullets: ['Fotos ilimitadas + AI Listing', 'Categorías, tags y ubicación', 'Chat interno con el comprador'],
        visual: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=900&q=80',
    },
    {
        id: 'okwallet',
        icon: Wallet,
        color: 'bg-emerald-50 text-emerald-700 border-emerald-100',
        title: 'OkWallet',
        value: 'Paga y cobra con tu billetera digital.',
        bullets: ['Recargas rápidas y transferencias P2P', 'Historial de transacciones', 'Puntos y cashback integrados'],
        mock: 'wallet',
    },
    {
        id: 'cashback',
        icon: Gift,
        color: 'bg-amber-50 text-amber-700 border-amber-100',
        title: 'Cashback y puntos',
        value: 'Recompensas reales por cada venta.',
        bullets: ['Hasta 15% de cashback en Elite', 'Puntos canjeables', 'Niveles por volumen'],
        mock: 'cashback',
    },
    {
        id: 'okenvios',
        icon: Truck,
        color: 'bg-blue-50 text-blue-700 border-blue-100',
        title: 'OkEnvíos',
        value: 'DHL, FedEx, Estafeta y Redpack en un solo lugar.',
        bullets: ['Cotización real en tiempo real', 'Guías con tracking automático', 'Direcciones guardadas'],
        visual: 'https://images.unsplash.com/photo-1620312584967-9d8d8e76d80e?w=900&q=80',
    },
    {
        id: 'comparativa',
        icon: TrendingUp,
        color: 'bg-violet-50 text-violet-700 border-violet-100',
        title: 'Comparativa entre vendedores',
        value: '"Otros vendedores lo tienen" al mejor precio total.',
        bullets: ['Precio + envío por artículo', 'Selector de paquetería', 'Rating y verificación del seller'],
        mock: 'compare',
    },
    {
        id: 'oklive',
        icon: Radio,
        color: 'bg-rose-50 text-rose-700 border-rose-100',
        title: 'OkLive Shop',
        value: 'Vende en vivo con miles de espectadores.',
        bullets: ['Streams con carrito activo', 'Reservas y descuentos flash', 'Minutos según tu plan'],
        visual: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=900&q=80',
    },
    {
        id: 'storefront',
        icon: Store,
        color: 'bg-indigo-50 text-indigo-700 border-indigo-100',
        title: 'Storefront personal',
        value: 'Tu propia tienda estilo Shopify, sin código.',
        bullets: ['Dominio personalizado', 'Colecciones y páginas', 'SEO y analítica'],
        mock: 'store',
    },
    {
        id: 'flash-sales',
        icon: Zap,
        color: 'bg-yellow-50 text-yellow-700 border-yellow-100',
        title: 'Ofertas relámpago',
        value: 'Urgencia real con temporizadores.',
        bullets: ['Programa 6h a 7 días', 'Descuento visible en el listado', 'Quota según plan'],
        mock: 'flash',
    },
    {
        id: 'subastas',
        icon: Gavel,
        color: 'bg-slate-100 text-slate-700 border-slate-200',
        title: 'Subastas y ofertas',
        value: 'Vende al mejor postor o negocia en privado.',
        bullets: ['Pujas con incrementos', 'Ofertas negociadas', 'Notificaciones en tiempo real'],
        mock: 'auction',
    },
    {
        id: 'reviews-kyc',
        icon: ShieldCheck,
        color: 'bg-emerald-50 text-emerald-700 border-emerald-100',
        title: 'Reseñas + KYC',
        value: 'Confianza total: compradores y vendedores verificados.',
        bullets: ['Rating de 5 estrellas', 'Badge verificado', 'Reportes moderados'],
        mock: 'trust',
    },
    {
        id: 'services',
        icon: Shirt,
        color: 'bg-sky-50 text-sky-700 border-sky-100',
        title: 'Servicios a domicilio',
        value: 'Lavandería y tintorería que recogen tu ropa.',
        bullets: ['Reserva por franja horaria', 'Chat con el proveedor', 'Reprogramación fácil'],
        visual: 'https://images.unsplash.com/photo-1610557892470-55d9e80c0bce?w=900&q=80',
    },
    {
        id: 'plans',
        icon: Crown,
        color: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
        title: 'Planes OkGo',
        value: 'De Free a Elite, escala cuando tu negocio crezca.',
        bullets: ['5 planes con features claros', 'Cambia cuando quieras', 'Descuento anual'],
        mock: 'plans',
    },
    {
        id: 'cfdi',
        icon: FileText,
        color: 'bg-neutral-100 text-neutral-700 border-neutral-200',
        title: 'Facturación CFDI',
        value: 'Factura.com integrado para operaciones formales.',
        bullets: ['Emite CFDI en 1 clic', 'Historial fiscal', 'Estatus SAT visible'],
        mock: 'cfdi',
    },
    {
        id: 'ai-listing',
        icon: Sparkles,
        color: 'bg-pink-50 text-pink-700 border-pink-100',
        title: 'AI Listing',
        value: 'Genera un anuncio desde 1 foto.',
        bullets: ['Título + descripción + precio sugerido', 'Categorías automáticas', 'Compatible con OpenRouter'],
        visual: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=900&q=80',
    },
    {
        id: 'chat',
        icon: MessagesSquare,
        color: 'bg-cyan-50 text-cyan-700 border-cyan-100',
        title: 'Chat interno',
        value: 'Coordina sin salir de la plataforma.',
        bullets: ['Notificaciones en tiempo real', 'Ligado al pedido y a la guía', 'Reportes de mensajes'],
        mock: 'chat',
    },
    {
        id: 'multidioma',
        icon: Languages,
        color: 'bg-teal-50 text-teal-700 border-teal-100',
        title: 'Multi-idioma',
        value: '7 idiomas para que vendas más lejos.',
        bullets: ['ES, EN, KO, JA, DE, FR, IT', 'UI localizada', 'Detección automática'],
        mock: 'i18n',
    },
];

// ---------- VISUAL MOCKS ----------
const Mock = ({ kind }) => {
    if (kind === 'wallet') return (
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-neutral-800 text-white p-5 shadow-lg">
            <div className="text-xs uppercase text-white/60">OkWallet</div>
            <div className="mt-2 text-3xl font-bold tabular-nums" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>$ 4,382.50</div>
            <div className="text-[11px] text-white/60 mt-1">Saldo disponible · MXN</div>
            <div className="mt-4 flex items-center gap-2">
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">+2.4% este mes</Badge>
                <Badge className="bg-white/10 text-white border-white/20">1,240 pts</Badge>
            </div>
        </div>
    );
    if (kind === 'cashback') return (
        <div className="rounded-2xl bg-white border p-5 shadow-sm">
            <div className="text-xs uppercase text-muted-foreground">Cashback acumulado</div>
            <div className="mt-2 text-3xl font-bold text-emerald-700 tabular-nums" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>$ 1,120.00</div>
            <div className="mt-3 space-y-1.5">
                {[80, 55, 35].map((w, i) => (
                    <div key={i} className="h-1.5 rounded-full bg-slate-100"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${w}%` }} /></div>
                ))}
            </div>
        </div>
    );
    if (kind === 'compare') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm text-sm">
            <div className="text-xs uppercase text-muted-foreground mb-2">Otros vendedores lo tienen</div>
            {[
                { name: '@marta.tienda', price: 890, ship: 129, best: true },
                { name: '@luis.electro', price: 920, ship: 149, best: false },
                { name: '@tienda.mx', price: 950, ship: 99, best: false },
            ].map((r, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2"><span className="font-medium">{r.name}</span>{r.best && <Badge className="bg-emerald-100 text-emerald-800">Mejor total</Badge>}</div>
                    <div className="tabular-nums text-xs" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>${r.price}+${r.ship} = <strong>${r.price + r.ship}</strong></div>
                </div>
            ))}
        </div>
    );
    if (kind === 'store') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm">
            <div className="flex items-center gap-2 pb-2 border-b">
                <div className="w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-bold">M</div>
                <div>
                    <div className="text-sm font-semibold">Marta's Store</div>
                    <div className="text-[10px] text-muted-foreground">okgo.mx/store/marta</div>
                </div>
                <Badge className="ml-auto bg-emerald-100 text-emerald-800">Verificado</Badge>
            </div>
            <div className="grid grid-cols-3 gap-1.5 mt-2">
                {[0, 1, 2].map(i => <div key={i} className="aspect-square rounded bg-slate-100" />)}
            </div>
        </div>
    );
    if (kind === 'flash') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm">
            <div className="flex items-center gap-2">
                <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1"><Zap className="h-3 w-3" />Flash</Badge>
                <span className="text-xs">-40%</span>
                <span className="ml-auto text-xs tabular-nums text-red-700 font-semibold">03:24:12</span>
            </div>
            <div className="mt-2 flex gap-2">
                <div className="w-14 h-14 rounded bg-slate-100" />
                <div className="flex-1">
                    <div className="text-sm font-semibold line-clamp-1">Sneakers Deportivos</div>
                    <div className="text-xs"><span className="line-through text-muted-foreground">$1,200</span> <span className="font-bold text-red-700">$720</span></div>
                </div>
            </div>
        </div>
    );
    if (kind === 'auction') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm text-sm">
            <div className="flex items-center gap-2 pb-2 border-b">
                <Gavel className="h-4 w-4 text-slate-700" /><span className="font-semibold">Subasta activa</span>
                <span className="ml-auto text-xs text-red-700 tabular-nums">2h 15m</span>
            </div>
            {[{ n: 'juan_m', b: 850 }, { n: 'ana_p', b: 800 }, { n: 'carlos', b: 750 }].map((b, i) => (
                <div key={i} className="flex items-center justify-between py-1 text-xs">
                    <span>@{b.n}</span>
                    <span className="tabular-nums font-mono">${b.b}</span>
                </div>
            ))}
        </div>
    );
    if (kind === 'trust') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-slate-200" />
                <div className="flex-1">
                    <div className="text-sm font-semibold flex items-center gap-1">Ana Ruiz <BadgeCheck className="h-3.5 w-3.5 text-blue-600" /></div>
                    <div className="text-xs text-muted-foreground">124 ventas · KYC verificado</div>
                </div>
            </div>
            <div className="flex items-center gap-1 mt-2">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />)}
                <span className="text-xs ml-1">4.9</span>
            </div>
        </div>
    );
    if (kind === 'plans') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm text-xs">
            {[
                { n: 'Free', p: 0 },
                { n: 'Starter', p: 149 },
                { n: 'Pro', p: 399, badge: true },
                { n: 'Elite', p: 1499 },
            ].map((p, i) => (
                <div key={i} className={`flex items-center justify-between py-1.5 border-b last:border-0 ${p.badge ? 'font-semibold' : ''}`}>
                    <span>{p.n}</span>
                    <span className="tabular-nums">{p.p === 0 ? 'Gratis' : `$${p.p}/mes`}</span>
                    {p.badge && <Badge className="bg-red-100 text-red-700 ml-1 text-[10px]">Popular</Badge>}
                </div>
            ))}
        </div>
    );
    if (kind === 'cfdi') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm text-xs" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            <div className="flex items-center gap-2 pb-2 border-b"><FileText className="h-4 w-4" /><span className="font-semibold">CFDI 4.0</span><Badge className="bg-emerald-100 text-emerald-800 ml-auto">Timbrado</Badge></div>
            <div>UUID: 12E3-A0F8-…</div>
            <div>Emisor: OKGO MX S.A.</div>
            <div>Total: $1,240.00 MXN</div>
        </div>
    );
    if (kind === 'chat') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm space-y-2">
            <div className="flex justify-start"><div className="bg-slate-100 px-3 py-1.5 rounded-2xl text-xs max-w-[75%]">¿Aún está disponible?</div></div>
            <div className="flex justify-end"><div className="bg-red-600 text-white px-3 py-1.5 rounded-2xl text-xs max-w-[75%]">¡Sí! ¿Cuándo lo quieres recoger?</div></div>
            <div className="flex justify-start"><div className="bg-slate-100 px-3 py-1.5 rounded-2xl text-xs max-w-[75%]">Hoy si es posible 🚀</div></div>
        </div>
    );
    if (kind === 'i18n') return (
        <div className="rounded-2xl bg-white border p-4 shadow-sm flex flex-wrap gap-2">
            {['🇲🇽 ES', '🇺🇸 EN', '🇰🇷 KO', '🇯🇵 JA', '🇩🇪 DE', '🇫🇷 FR', '🇮🇹 IT'].map(l => <Badge key={l} variant="outline" className="text-xs">{l}</Badge>)}
        </div>
    );
    return null;
};

// ---------- SUPERPOWER SECTION ----------
const SuperpowerSection = ({ item, reverse, onCta, isLoginMode }) => {
    const Icon = item.icon;
    return (
        <section className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center ${reverse ? 'md:[direction:rtl]' : ''}`} data-testid={`superpoderes-section-${item.id}`}>
            <div className="md:[direction:ltr]">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${item.color} text-xs font-semibold`}>
                    <Icon className="h-3.5 w-3.5" /> {item.title}
                </div>
                <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {item.value}
                </h2>
                <ul className="mt-4 space-y-2">
                    {item.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground/85">
                            <span className={`inline-flex mt-0.5 shrink-0 h-4 w-4 rounded-full ${item.color.split(' ')[0]} items-center justify-center`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                            </span>
                            {b}
                        </li>
                    ))}
                </ul>
                <Button onClick={onCta} className="mt-6 rounded-xl" data-testid={`superpoderes-register-cta-${item.id}`}>
                    {isLoginMode ? 'Accede a' : 'Prueba'} {item.title} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
            </div>
            <div className="md:[direction:ltr] relative">
                <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-red-100/40 via-transparent to-slate-100/40 -z-10 blur-2xl" />
                {item.mock ? (
                    <Mock kind={item.mock} />
                ) : (
                    <div className="rounded-3xl overflow-hidden aspect-[4/3] bg-slate-100 shadow-lg hover:shadow-xl transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5">
                        <img src={item.visual} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                )}
            </div>
        </section>
    );
};

// ---------- ADS DEMO ----------
const AdsDemoGrid = ({ onCta, isLoginMode }) => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get('/ads', { params: { limit: 12, sort: 'recent' } })
            .then(({ data }) => setAds(data.items || []))
            .catch(() => setAds([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="ads-demo-section">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
                <div>
                    <div className="text-xs uppercase text-red-600 font-semibold tracking-widest">Anuncios reales</div>
                    <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Explora un poco del catálogo</h2>
                </div>
                <Button variant="outline" onClick={onCta} data-testid="ads-demo-register-cta">{isLoginMode ? 'Ver todo tras acceder' : 'Ver todo tras registro'}</Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="ads-demo-grid">
                {loading ? (
                    Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-2xl" data-testid="ads-demo-loading" />)
                ) : ads.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-sm text-muted-foreground" data-testid="ads-demo-empty">
                        Aún no hay anuncios. <button onClick={onCta} className="text-red-700 underline">{isLoginMode ? 'Accede' : 'Regístrate'}</button> y sé el primero.
                    </div>
                ) : ads.map((ad, i) => (
                    <button key={ad.id} onClick={onCta} className="text-left group" data-testid={`ads-demo-card-${i}`}>
                        <Card className="overflow-hidden hover:shadow-lg transition-[box-shadow,transform] duration-200 group-hover:-translate-y-0.5">
                            <div className="aspect-[4/5] bg-slate-100 overflow-hidden">
                                <img src={mediaUrl(ad.media?.[0]?.url)} alt={ad.title} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <CardContent className="p-3">
                                <div className="text-sm font-semibold line-clamp-2 min-h-[2.5em]">{ad.title}</div>
                                <div className="flex items-center justify-between mt-1">
                                    <div className="font-bold tabular-nums" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatMoney(ad.price)}</div>
                                    {ad.location && <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground"><MapPin className="h-2.5 w-2.5" />{ad.location.split(',')[0]}</div>}
                                </div>
                            </CardContent>
                        </Card>
                    </button>
                ))}
            </div>
        </section>
    );
};

// ---------- BENTO ----------
const BentoTile = ({ item, className }) => {
    const Icon = item.icon;
    return (
        <div className={`rounded-2xl border p-5 bg-white shadow-sm hover:shadow-md transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 ${className}`} data-testid={`superpoderes-feature-tile-${item.id}`}>
            <div className={`inline-flex items-center justify-center h-10 w-10 rounded-xl ${item.color}`}>
                <Icon className="h-5 w-5" />
            </div>
            <div className="mt-3 text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{item.title}</div>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.value}</p>
        </div>
    );
};

// ---------- FAQ ----------
const FAQ = () => (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Preguntas frecuentes</h2>
        <Accordion type="single" collapsible className="w-full">
            {[
                { q: '¿Es realmente gratis?', a: 'El plan Free te permite publicar y vender. Los planes de pago desbloquean funciones avanzadas: envíos ilimitados, live shopping, servicios y más.' },
                { q: '¿Cómo cobro?', a: 'Puedes cobrar directamente a tu OkWallet (retirable a cuenta bancaria) o vía Stripe si activas pagos con tarjeta.' },
                { q: '¿OkEnvíos es obligatorio?', a: 'No. Puedes usar envío por cuenta propia o cotizar con OkEnvíos (DHL/FedEx/Estafeta/Redpack) al momento.' },
                { q: '¿Puedo tener mi tienda con dominio propio?', a: 'Sí. Con planes Pro+ puedes conectar tu dominio a tu storefront y personalizar branding.' },
                { q: '¿Emiten facturas CFDI?', a: 'Sí. Integramos Factura.com para emitir CFDI 4.0 desde tus ventas o servicios.' },
            ].map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                    <AccordionContent>{f.a}</AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    </section>
);

// ---------- PAGE ----------
export default function SuperpoderesPage({ ctaMode = 'signup' } = {}) {
    const [heroImages, setHeroImages] = useState([]);
    const promptLogin = usePromptLogin();
    const isLoginMode = ctaMode === 'login';
    const onCta = isLoginMode ? () => promptLogin({ intent: 'generic' }) : openSignup;
    useEffect(() => {
        api.get('/public/settings').then(({ data }) => setHeroImages(data.hero_images || [])).catch(() => setHeroImages([]));
    }, []);

    const bentoFirst = SUPERPOWERS.slice(0, 8);

    return (
        <div className="min-h-screen bg-[hsl(var(--background))] text-foreground" data-testid="superpoderes-page">
            {heroImages.length > 0 && <HeroCarousel images={heroImages} onCta={onCta} isLoginMode={isLoginMode} />}

            {/* Bento resumen */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="text-center mb-8">
                    <div className="text-xs uppercase text-red-600 font-semibold tracking-widest">Tu equipo, tus superpoderes</div>
                    <h2 className="text-3xl sm:text-4xl font-bold mt-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Todo lo que puedes hacer</h2>
                    <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">Un solo lugar para vender, cobrar, enviar, transmitir en vivo y hacer crecer tu negocio.</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                    {bentoFirst.map(item => <BentoTile key={item.id} item={item} />)}
                </div>
            </section>

            <CTARegisterStrip testId="superpoderes-register-cta-mid" onCta={onCta} isLoginMode={isLoginMode} />

            {/* Anuncios demo */}
            <AdsDemoGrid onCta={onCta} isLoginMode={isLoginMode} />

            {/* Superpoderes detallados */}
            <div className="bg-[hsl(var(--background))]" data-testid="superpoderes-features-list">
                {SUPERPOWERS.map((item, i) => (
                    <div key={item.id} className={i % 2 === 1 ? 'bg-slate-50/60' : ''}>
                        <SuperpowerSection item={item} reverse={i % 2 === 1} onCta={onCta} isLoginMode={isLoginMode} />
                    </div>
                ))}
            </div>

            <CTARegisterStrip variant="dark" testId="superpoderes-register-cta-before-faq" onCta={onCta} isLoginMode={isLoginMode} />

            <FAQ />

            {/* Footer minimalista */}
            <footer className="border-t bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Ok te lo vendo · OkGo</span>
                        <Badge variant="outline">© {new Date().getFullYear()}</Badge>
                    </div>
                    <Button onClick={onCta} className="rounded-xl" data-testid="superpoderes-register-cta-footer">
                        {isLoginMode ? 'Acceder' : 'Regístrate'} <ExternalLink className="h-4 w-4 ml-1" />
                    </Button>
                </div>
            </footer>
        </div>
    );
}
